import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  FolderOpen,
  AlertCircle,
  Loader,
  ChevronRight,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Job, JobStatus, StepStatus, JobType } from '../graphql/types';
import * as workflowService from '../services/workflowService';
import { graphqlClient } from '../services/graphqlClient';
import { ConfirmModal } from './Modal';
import { getDraftPath } from '../workspace/draftModel';
import { FEATURE_HOME_PATH } from '../workspace/modes';
import { EmptyState, Pill, StatCard } from './ui';
import {
  GRADE_ATTENTION_CLASSES,
  PLACEHOLDER,
  buildHomeRow,
  computeStats,
  filterRowsByStat,
  getCountPill,
  getFeatureHomeConfig,
  getSummary,
  gradeTone,
  resolveGuidelineName,
  type ColumnDef,
  type HomeRow,
  type StatId,
} from './featureHome/featureHomeModel';
import { useGuidelineNames } from './featureHome/useGuidelineNames';
import { mergeJobDetails, useTitleSearchRowDetails } from './featureHome/useTitleSearchRowDetails';

/**
 * FeatureHome (file kept as WorkflowLandingPage): the landing list for one
 * Feature. Title, stat cards, columns and the create label come from the
 * per-Feature config in `featureHome/featureHomeModel`, so Due Diligence reads
 * "Jobs / New job", Borrower Analysis "Loans / New loan" and Title Search
 * "Title searches / New title search".
 *
 * Stat cards are computed from the same rows the list shows, and selecting one
 * filters the list with the same predicate, so counts and rows agree.
 *
 * Requirements: 2.3, 2.4, 2.5, 2.6, 11.11
 */

interface WorkflowLandingPageProps {
  jobType: JobType;
}

const ITEMS_PER_PAGE = 10;
const MAX_RECONNECT_ATTEMPTS = 3;
const POLLING_INTERVAL_MS = 5000;

// Check if we're in preview mode
const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' ||
  !import.meta.env.VITE_COGNITO_USER_POOL_ID;

const WorkflowLandingPage: React.FC<WorkflowLandingPageProps> = ({ jobType }) => {
  const navigate = useNavigate();
  const config = getFeatureHomeConfig(jobType);

  // Route prefix for this Feature's Workspace routes. Each Feature has a
  // sibling top-level route (/due-diligence, /borrower-analysis, /title-search).
  const basePath = FEATURE_HOME_PATH[jobType];

  // State
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The selected stat card; 'total' shows every row.
  const [statFilter, setStatFilter] = useState<StatId>('total');
  const [sortBy, setSortBy] = useState<'date' | 'status'>('date');
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  // Toggle between active items and the archived view.
  const [viewArchived, setViewArchived] = useState(false);

  // Modal state
  const [confirmArchive, setConfirmArchive] = useState<Job | null>(null);
  const [archivingJob, setArchivingJob] = useState<string | null>(null);
  const [restoringJob, setRestoringJob] = useState<string | null>(null);

  // Subscription state for reconnection and polling fallback
  // Requirements: 12.1, 12.5, 12.6
  const reconnectAttemptsRef = useRef(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const subscriptionRef = useRef<any>(null);
  const isPollingRef = useRef(false);

  /**
   * Load jobs from the API
   * Requirements: 3.1, 4.1 - Display jobs belonging to the current user filtered by job type
   */
  const loadJobs = useCallback(async (token?: string) => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
      const username = session.tokens?.idToken?.payload['cognito:username'] as string || 'unknown';

      // DynamoDB applies the server-side filter AFTER the page limit, so a
      // response can come back with 0 items and still have a nextToken. Keep
      // chaining pages until we've either filled the requested page size or
      // exhausted the table. Bounded by a safety cap so a pathological filter
      // can't turn this into a full scan.
      const MAX_CHAIN = 10;
      let collected: Job[] = [];
      let cursor: string | undefined = token;
      let finalToken: string | null = null;
      for (let i = 0; i < MAX_CHAIN; i++) {
        const result = await workflowService.listJobsByUser(
          tenantId,
          username,
          jobType,
          ITEMS_PER_PAGE,
          cursor,
          viewArchived
        );
        collected = collected.concat(result.items);
        finalToken = result.nextToken;
        if (!finalToken || collected.length >= ITEMS_PER_PAGE) break;
        cursor = finalToken;
      }

      if (token) {
        setJobs(prev => [...prev, ...collected]);
      } else {
        setJobs(collected);
      }

      setNextToken(finalToken);
      setHasNextPage(!!finalToken);
      setError(null);
    } catch (err) {
      console.error('Failed to load jobs:', err);
      setError(`We couldn't load your ${config.unitPlural}. Check your connection and try again.`);
    } finally {
      setLoading(false);
    }
  }, [jobType, viewArchived, config.unitPlural]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  /**
   * Handle real-time job status updates via subscription
   * Requirements: 12.1, 12.2, 12.6 - Subscribe to job status changes filtered by jobType
   */
  const handleSubscriptionUpdate = useCallback((updatedJob: Job) => {
    // Requirement 12.6: Filter updates to show only jobs matching the current jobType
    if (updatedJob.jobType !== jobType) {
      return;
    }

    // The two views (active / archived) are mutually exclusive, so only accept
    // updates that belong in the current view. A job that was just archived (or
    // unarchived) falls out of this view naturally on the next refresh.
    const belongsInView = viewArchived
      ? updatedJob.status === JobStatus.ARCHIVED
      : updatedJob.status !== JobStatus.ARCHIVED;
    if (!belongsInView) {
      setJobs(prev => prev.filter(j => j.jobId !== updatedJob.jobId));
      return;
    }

    // Requirement 12.2: Update job display without page refresh
    setJobs(prev => {
      const index = prev.findIndex(j => j.jobId === updatedJob.jobId);
      if (index >= 0) {
        const updated = [...prev];
        updated[index] = updatedJob;
        return updated;
      }
      // New job - add to the beginning of the list
      return [updatedJob, ...prev];
    });
  }, [jobType, viewArchived]);

  /**
   * Start polling fallback when subscription fails
   * Requirement 12.5: Fall back to polling every 5 seconds after 3 failed reconnection attempts
   */
  const startPollingFallback = useCallback(async () => {
    if (isPollingRef.current) return;
    
    isPollingRef.current = true;
    console.log('Starting polling fallback for workflow job updates');

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const session = await fetchAuthSession();
        const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
        const username = session.tokens?.idToken?.payload['cognito:username'] as string || 'unknown';

        // Same chain-until-full logic as the primary loader. See loadJobs for why.
        const MAX_CHAIN = 10;
        let collected: Job[] = [];
        let cursor: string | undefined;
        let finalToken: string | null = null;
        for (let i = 0; i < MAX_CHAIN; i++) {
          const result = await workflowService.listJobsByUser(
            tenantId,
            username,
            jobType,
            ITEMS_PER_PAGE,
            cursor,
            viewArchived
          );
          collected = collected.concat(result.items);
          finalToken = result.nextToken;
          if (!finalToken || collected.length >= ITEMS_PER_PAGE) break;
          cursor = finalToken;
        }

        setJobs(collected);
        setNextToken(finalToken);
        setHasNextPage(!!finalToken);
      } catch (err) {
        console.error('Polling failed:', err);
      }
    }, POLLING_INTERVAL_MS);
  }, [jobType, viewArchived]);

  /**
   * Stop polling fallback
   */
  const stopPollingFallback = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  /**
   * Setup subscription with reconnection logic
   * Requirements: 12.1, 12.5 - Subscribe to onJobStatusChanged with reconnection
   */
  const setupSubscription = useCallback(async () => {
    // Skip subscription in preview mode - use polling instead
    if (isPreviewMode) {
      console.log('Preview mode: Skipping real-time subscription');
      return;
    }

    try {
      const session = await fetchAuthSession();
      
      // Check if we have valid tokens
      if (!session.tokens?.idToken) {
        console.warn('No valid auth token available for subscription');
        // Fall back to polling if no valid token
        startPollingFallback();
        return;
      }
      
      const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
      const username = session.tokens?.idToken?.payload['cognito:username'] as string || 'unknown';

      // Clean up existing subscription
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }

      // Stop polling if we're reconnecting successfully
      stopPollingFallback();

      const sub = graphqlClient.subscriptions.onJobStatusChanged(tenantId, username, jobType);
      subscriptionRef.current = (sub as any).subscribe({
        next: ({ data }: any) => {
          if (data?.onJobStatusChanged) {
            // Reset reconnect attempts on successful message
            reconnectAttemptsRef.current = 0;
            handleSubscriptionUpdate(data.onJobStatusChanged);
          }
        },
        error: async (err: any) => {
          console.error('Subscription error:', err);
          
          // Check if it's an auth error - don't retry, just fall back to polling
          const errorMessage = err?.message || err?.toString() || '';
          if (errorMessage.includes('NotAuthorizedException') || 
              errorMessage.includes('Invalid login token') ||
              errorMessage.includes('Unauthorized')) {
            console.log('Auth error in subscription, falling back to polling');
            startPollingFallback();
            return;
          }
          
          // Requirement 12.5: Attempt reconnection on subscription connection loss
          reconnectAttemptsRef.current += 1;
          
          if (reconnectAttemptsRef.current <= MAX_RECONNECT_ATTEMPTS) {
            console.log(`Attempting subscription reconnection (${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`);
            // Wait a bit before reconnecting
            setTimeout(() => {
              setupSubscription();
            }, 1000 * reconnectAttemptsRef.current);
          } else {
            // Requirement 12.5: Fall back to polling after 3 failed reconnection attempts
            console.log('Max reconnection attempts reached, falling back to polling');
            startPollingFallback();
          }
        },
      });

      return () => {
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
        }
      };
    } catch (err: any) {
      console.error('Failed to setup subscription:', err);
      
      // Check if it's an auth error - don't retry, just fall back to polling
      const errorMessage = err?.message || err?.toString() || '';
      if (errorMessage.includes('NotAuthorizedException') || 
          errorMessage.includes('Invalid login token') ||
          errorMessage.includes('Unauthorized')) {
        console.log('Auth error during subscription setup, falling back to polling');
        startPollingFallback();
        return;
      }
      
      // If initial setup fails, try reconnection logic
      reconnectAttemptsRef.current += 1;
      if (reconnectAttemptsRef.current <= MAX_RECONNECT_ATTEMPTS) {
        setTimeout(() => {
          setupSubscription();
        }, 1000 * reconnectAttemptsRef.current);
      } else {
        startPollingFallback();
      }
    }
  }, [jobType, handleSubscriptionUpdate, startPollingFallback, stopPollingFallback]);

  /**
   * Setup real-time subscription on mount
   * Requirements: 12.1, 12.5, 12.6
   */
  useEffect(() => {
    setupSubscription();

    return () => {
      // Cleanup subscription
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      // Cleanup polling
      stopPollingFallback();
      // Reset reconnect attempts
      reconnectAttemptsRef.current = 0;
    };
  }, [setupSubscription, stopPollingFallback]);

  /**
   * "New …" opens the Workspace for this Feature in Intake Mode as a draft
   * (other Modes locked until the item is created), not a create modal.
   * Requirements: 2.3, 3.5
   */
  const handleNewItem = () => {
    navigate(getDraftPath(jobType));
  };

  /**
   * Open the Workspace for a row. The Workspace picks the Mode that matches
   * the item's stage (Row → Mode) when the URL has no `mode`.
   * Requirement 2.6
   */
  const handleJobClick = (job: Job) => {
    navigate(`${basePath}/${job.jobId}`);
  };

  /**
   * Handle job archive (soft delete). Available for every job regardless of status.
   */
  const handleArchiveJob = (job: Job) => {
    setConfirmArchive(job);
  };

  const handleArchiveConfirm = async (job: Job) => {
    setConfirmArchive(null);
    setArchivingJob(job.jobId);
    try {
      const session = await fetchAuthSession();
      const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
      await workflowService.archiveJob(job.jobId, tenantId);
      setJobs(prev => prev.filter(j => j.jobId !== job.jobId));
    } catch (err) {
      console.error('Failed to archive job:', err);
      setError(`We couldn't archive that ${config.unitSingular}. Try again.`);
    } finally {
      setArchivingJob(null);
    }
  };

  /**
   * Restore an archived job to the active list. We infer a sensible status
   * from the existing step statuses — if every step had completed, the job
   * returns as COMPLETED; otherwise we put it back in the first pending step.
   */
  const handleRestoreJob = async (job: Job) => {
    setRestoringJob(job.jobId);
    try {
      const session = await fetchAuthSession();
      const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';

      let target: JobStatus;
      if (
        job.documentProcessingStatus === StepStatus.COMPLETED &&
        job.documentVerificationStatus === StepStatus.COMPLETED &&
        job.investorGuidelineReviewStatus === StepStatus.COMPLETED
      ) {
        target = JobStatus.COMPLETED;
      } else if (job.documentVerificationStatus === StepStatus.COMPLETED) {
        target = jobType === JobType.BA ? JobStatus.UNDERWRITING_GUIDELINE_REVIEW : JobStatus.INVESTOR_GUIDELINE_REVIEW;
      } else if (job.documentProcessingStatus === StepStatus.COMPLETED) {
        target = JobStatus.DOCUMENT_VERIFICATION;
      } else {
        target = JobStatus.DOCUMENT_PROCESSING;
      }

      await workflowService.setJobStatus(job.jobId, tenantId, target);
      setJobs(prev => prev.filter(j => j.jobId !== job.jobId));
    } catch (err) {
      console.error('Failed to restore job:', err);
      setError(`We couldn't restore that ${config.unitSingular}. Try again.`);
    } finally {
      setRestoringJob(null);
    }
  };

  /**
   * Handle pagination - load more results
   * Requirements: 3.8, 4.8
   */
  const handleLoadMore = () => {
    if (hasNextPage && !loading && nextToken) {
      loadJobs(nextToken);
    }
  };

  // In the active view, hide archived jobs client-side so subscription pushes
  // don't accidentally resurrect them. In the archived view, only archived
  // jobs are expected (the server enforces this).
  const viewJobs = useMemo(
    () => jobs.filter(job => (viewArchived ? job.status === JobStatus.ARCHIVED : job.status !== JobStatus.ARCHIVED)),
    [jobs, viewArchived]
  );

  // TR rows need their property, borrower, statuses and grade, which the job
  // list does not carry yet (Backend gap 5). No requests for DD/BA.
  const trDetails = useTitleSearchRowDetails(viewJobs, jobType === JobType.TR);

  // BA rows name their guideline; the job only remembers which one by id.
  const guidelineNames = useGuidelineNames(jobType === JobType.BA);

  // Every row in the current view. Stat cards count these rows, and the list
  // below shows these rows (narrowed by the selected card), so they agree.
  const rows: HomeRow[] = useMemo(
    () =>
      viewJobs.map(job => {
        const extra = trDetails.get(job.jobId);
        const effective = extra?.job ? mergeJobDetails(job, extra.job) : job;
        const guidelineName =
          jobType === JobType.BA ? resolveGuidelineName(job.jobId, guidelineNames) : null;
        return buildHomeRow(effective, extra?.grade ?? null, guidelineName);
      }),
    [viewJobs, trDetails, guidelineNames, jobType]
  );

  const stats = useMemo(() => computeStats(rows, config), [rows, config]);

  const shownRows = useMemo(() => {
    const filtered = viewArchived ? rows : filterRowsByStat(rows, statFilter);
    return [...filtered].sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.job.createdAt).getTime() - new Date(a.job.createdAt).getTime();
      }
      return a.stageLabel.localeCompare(b.stageLabel);
    });
  }, [rows, statFilter, sortBy, viewArchived]);

  const summary = viewArchived
    ? `Archived ${config.unitPlural} stay out of your active list. Restore one to bring it back.`
    : rows.length === 0
    ? `Create a ${config.unitSingular} to get started.`
    : getSummary(rows, config, hasNextPage);

  const renderCell = (row: HomeRow, column: ColumnDef): React.ReactNode => {
    switch (column.id) {
      case 'stage':
        // Title Search keeps its pill; Due Diligence and Borrower Analysis show
        // coloured text (boards 2a and 2b): red when blocked, green when done.
        if (row.job.jobType === JobType.TR) {
          return <Pill tone={row.stageTone}>{row.stageLabel}</Pill>;
        }
        return (
          <span
            className={`whitespace-nowrap text-[13px] font-bold ${
              row.stageEmphasis === 'blocked'
                ? 'text-[#b91c1c]'
                : row.stageEmphasis === 'done'
                ? 'text-[#15803d]'
                : 'text-gray-700'
            }`}
          >
            {row.stageLabel}
          </span>
        );
      case 'needsYou': {
        // A count badge: turbo yellow while waiting, red tint when blocked,
        // "—" when nothing waits. With no count the action text is the label,
        // and it is the tooltip either way.
        if (!row.needsYou) return <span className="text-gray-400">{PLACEHOLDER}</span>;
        const tone = row.blocked
          ? 'bg-[#fee2e2] text-[#991b1b]'
          : 'bg-[#F0E100] text-[#3C1A80]';
        return (
          <span
            title={row.needsYou}
            aria-label={row.needsYouCount !== null ? row.needsYou : undefined}
            className={`inline-flex h-[22px] min-w-[26px] items-center justify-center whitespace-nowrap rounded-full px-2 text-xs font-bold ${tone}`}
          >
            {row.needsYouCount ?? row.needsYou}
          </span>
        );
      }
      case 'grade': {
        // Grade of the latest completed run; "—" while there is none or it
        // has not loaded (Requirement 11.11).
        if (!row.grade) return <span className="text-gray-400">{PLACEHOLDER}</span>;
        return gradeTone(row.grade) === 'success' ? (
          <Pill tone="success" title={`Grade ${row.grade}`}>{row.grade}</Pill>
        ) : (
          <span
            title={`Grade ${row.grade}`}
            className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-bold ${GRADE_ATTENTION_CLASSES}`}
          >
            {row.grade}
          </span>
        );
      }
      default: {
        const value = row.cells[column.id] ?? PLACEHOLDER;
        return value === PLACEHOLDER ? <span className="text-gray-400">{value}</span> : value;
      }
    }
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="mx-auto flex h-64 max-w-6xl items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-[#834DFB]" aria-label="Loading" />
      </div>
    );
  }

  const createButton = (
    <button
      type="button"
      onClick={handleNewItem}
      style={{ backgroundColor: config.accent }}
      className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold text-white transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[#834DFB] focus:ring-offset-2"
    >
      <Plus className="h-4 w-4" strokeWidth={2} />
      {config.createLabel}
    </button>
  );

  const filterActive = !viewArchived && statFilter !== 'total';
  const activeStat = config.stats.find(s => s.id === statFilter);

  return (
    <div className="mx-auto min-w-0 max-w-[1200px]">
      {/* Header: title with count pill, one-line summary, primary action */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-[28px] font-bold leading-tight text-gray-900">
              {viewArchived ? config.archivedTitle : config.title}
            </h1>
            <Pill tone="selected" title={`${rows.length} ${rows.length === 1 ? config.unitSingular : config.unitPlural}`}>
              {viewArchived ? rows.length : getCountPill(rows, config)}
            </Pill>
          </div>
          <p className="mt-1 text-sm text-gray-600">{summary}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              setViewArchived(v => !v);
              setStatFilter('total');
            }}
            aria-pressed={viewArchived}
            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-bold transition-colors ${
              viewArchived
                ? 'border-[#834DFB] bg-[#F5F3FF] text-[#5A21C9]'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Archive className="h-4 w-4" strokeWidth={2} />
            {viewArchived ? `Show active ${config.unitPlural}` : 'Show archived'}
          </button>
          {!viewArchived && createButton}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div
          role="alert"
          className="mb-6 flex items-center gap-3 rounded-xl border border-[#fecaca] bg-[#fee2e2] p-4 text-sm text-[#991b1b]"
        >
          <AlertCircle className="h-4 w-4 shrink-0" strokeWidth={2} />
          <p className="flex-1">{error}</p>
          <button
            type="button"
            onClick={() => {
              setError(null);
              loadJobs();
            }}
            className="rounded-lg border border-[#991b1b] px-3 py-1 text-xs font-bold hover:bg-white/60"
          >
            Retry
          </button>
        </div>
      )}

      {/* Four stat cards. Each counts the rows matching its predicate and
          filters the list to those rows when selected (Requirement 2.4). */}
      {!viewArchived && (
        <div className="mb-6 grid grid-cols-4 gap-4">
          {stats.map(stat => {
            const selected = statFilter === stat.id;
            return (
              <button
                key={stat.id}
                type="button"
                aria-pressed={selected}
                aria-label={`${stat.label}: ${stat.count}. ${selected ? 'Showing these rows' : 'Show these rows'}`}
                onClick={() => setStatFilter(selected ? 'total' : stat.id)}
                className={`rounded-xl text-left transition focus:outline-none focus:ring-2 focus:ring-[#834DFB] ${
                  selected ? 'ring-2 ring-[#834DFB]' : 'hover:ring-1 hover:ring-[#EDE7FF]'
                }`}
              >
                <StatCard label={stat.label} value={stat.count} hint={stat.hint} className="h-full" />
              </button>
            );
          })}
        </div>
      )}

      {/* List controls */}
      {!viewArchived && rows.length > 0 && (
        <div className="mb-3 flex items-center justify-between gap-3 text-sm text-gray-600">
          <div>
            {filterActive && activeStat ? (
              <span>
                Showing {shownRows.length} of {rows.length}: {activeStat.label.toLowerCase()}.{' '}
                <button
                  type="button"
                  onClick={() => setStatFilter('total')}
                  className="font-bold text-[#5A21C9] underline hover:text-[#6E2EF0]"
                >
                  Show all
                </button>
              </span>
            ) : null}
          </div>
          <label className="flex items-center gap-2">
            <span className="sr-only">Sort {config.unitPlural}</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'status')}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#834DFB]"
            >
              <option value="date">Newest first</option>
              <option value="status">By stage</option>
            </select>
          </label>
        </div>
      )}

      {/* List */}
      {shownRows.length === 0 ? (
        <EmptyState
          icon={<FolderOpen className="h-6 w-6" strokeWidth={2} />}
          title={
            viewArchived
              ? `No archived ${config.unitPlural}`
              : filterActive
              ? `No ${config.unitPlural} match this card`
              : `No ${config.unitPlural} yet`
          }
          description={
            viewArchived
              ? `${config.unitPlural.charAt(0).toUpperCase()}${config.unitPlural.slice(1)} you archive will show up here.`
              : filterActive
              ? 'Select the card again to show every row.'
              : `Start with ${config.createLabel.toLowerCase()}. It opens in Intake.`
          }
          action={!viewArchived && !filterActive ? createButton : undefined}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="w-full border-collapse text-sm text-gray-700">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                {config.columns.map(column => (
                  <th
                    key={column.id}
                    scope="col"
                    className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500"
                  >
                    {column.header}
                  </th>
                ))}
                <th scope="col" className="w-28 px-4 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shownRows.map(row => {
                const { job } = row;
                const label = job.name?.trim() || `${config.unitSingular} ${job.jobId.slice(0, 8)}`;
                return (
                  <tr
                    key={job.jobId}
                    onClick={() => handleJobClick(job)}
                    className="cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-[#F5F3FF]"
                  >
                    {config.columns.map((column, index) => (
                      <td key={column.id} className="px-4 py-3 align-middle">
                        {index === 0 ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleJobClick(job);
                            }}
                            aria-label={`Open ${label}`}
                            className="block max-w-[280px] text-left hover:text-[#5A21C9] focus:outline-none focus:ring-2 focus:ring-[#834DFB]"
                          >
                            <span className="block truncate text-[15px] font-bold text-gray-900">
                              {renderCell(row, column)}
                            </span>
                            {row.primarySub && (
                              <span
                                title={row.primarySub}
                                className="mt-0.5 block truncate text-[13px] font-normal text-gray-500"
                              >
                                {row.primarySub}
                              </span>
                            )}
                          </button>
                        ) : (
                          renderCell(row, column)
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right align-middle">
                      {/* Row action: Archive in the active view, Restore in the archived view */}
                      {viewArchived ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestoreJob(job);
                          }}
                          disabled={restoringJob === job.jobId}
                          title={`Restore ${config.unitSingular}`}
                          aria-label={`Restore ${label}`}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-[#834DFB] px-3 py-1.5 text-xs font-bold text-[#5A21C9] hover:bg-[#F5F3FF] focus:outline-none focus:ring-2 focus:ring-[#834DFB] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {restoringJob === job.jobId ? (
                            <Loader className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <ArchiveRestore className="h-3.5 w-3.5" strokeWidth={2} />
                          )}
                          Restore
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchiveJob(job);
                          }}
                          disabled={archivingJob === job.jobId}
                          title={`Archive ${config.unitSingular}`}
                          aria-label={`Archive ${label}`}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#834DFB] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {archivingJob === job.jobId ? (
                            <Loader className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Archive className="h-3.5 w-3.5" strokeWidth={2} />
                          )}
                          Archive
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination - Requirements: 3.8, 4.8 */}
      {hasNextPage && (
        <div className="flex justify-center pt-6">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-[#834DFB] bg-white px-5 py-2 text-sm font-bold text-[#5A21C9] hover:bg-[#F5F3FF] focus:outline-none focus:ring-2 focus:ring-[#834DFB] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                Loading
              </>
            ) : (
              <>
                <ChevronRight className="h-4 w-4" strokeWidth={2} />
                Load more {config.unitPlural}
              </>
            )}
          </button>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {confirmArchive && (
        <ConfirmModal
          title={`Archive ${config.unitSingular}`}
          message={`Archive "${confirmArchive.name || confirmArchive.jobId.slice(0, 8)}"? It will leave your active ${config.unitPlural}. You can bring it back from "Show archived".`}
          confirmLabel="Archive"
          variant="default"
          onConfirm={() => handleArchiveConfirm(confirmArchive)}
          onCancel={() => setConfirmArchive(null)}
        />
      )}
    </div>
  );
};

export { WorkflowLandingPage as FeatureHome };
export default WorkflowLandingPage;
